import React from 'react';
import { Box, Chip, InputAdornment, OutlinedInput } from '@mui/material';
import type { AutocompleteRenderGetTagProps, AutocompleteRenderInputParams, AutocompleteRenderOptionState } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';

import { IconButton } from '../Buttons/IconButton';
import { InlineSpinner } from '../Buttons/InlineSpinner';

import type { FieldIds, FieldSize, OptionKey, OptionState } from './field.types';
import { describedBy } from './FieldShell';
import { controlTextSx, visuallyHiddenSx } from './fieldStyles';
import type { TextBoxHandlers } from './useAutocompleteField';

interface TextBoxActionsProps {
  readonly busy: boolean;
  readonly onClear: ((event: React.MouseEvent) => void) | null;
}

const TextBoxActions = ({ busy, onClear }: TextBoxActionsProps) => {
  const { t } = useTranslation();
  if (!busy && onClear === null) return null;
  return (
    <InputAdornment position="end">
      {busy && <InlineSpinner color="primary" aria-hidden />}
      {onClear !== null && (
        <IconButton label={t('coreComponents.fields.clear', 'Clear')} onClick={onClear}>
          <ClearIcon fontSize="small" />
        </IconButton>
      )}
    </InputAdornment>
  );
};

export interface AutocompleteTextBoxProps extends TextBoxHandlers {
  readonly params: AutocompleteRenderInputParams;
  readonly ids: FieldIds;
  readonly helperText: React.ReactNode;
  readonly describedById: string | undefined;
  readonly placeholder: string | undefined;
  readonly size: FieldSize;
  readonly busy: boolean;
  readonly onClear: ((event: React.MouseEvent) => void) | null;
}

export const AutocompleteTextBox = ({ params, ids, helperText, describedById, placeholder, size, busy, onClear, onBlur, onKeyDown }: AutocompleteTextBoxProps) => (
  <OutlinedInput
    ref={params.InputProps.ref}
    className={params.InputProps.className}
    startAdornment={params.InputProps.startAdornment}
    endAdornment={
      <>
        <TextBoxActions busy={busy} onClear={onClear} />
        {params.InputProps.endAdornment}
      </>
    }
    inputProps={{ ...params.inputProps, 'aria-describedby': describedBy(ids, helperText, describedById) }}
    onBlur={onBlur}
    onKeyDown={onKeyDown}
    placeholder={placeholder}
    notched={false}
    disabled={params.disabled}
    fullWidth
    sx={controlTextSx(size)}
  />
);

export const AutocompleteStatus = ({ children }: { readonly children: React.ReactNode }) => (
  <Box role="status" sx={visuallyHiddenSx}>
    {children}
  </Box>
);

type OptionItemProps = React.HTMLAttributes<HTMLLIElement> & { key: React.Key };

export const optionRenderer =
  <T,>(getOptionLabel: (option: T) => string, renderOption: ((option: T, state: OptionState) => React.ReactNode) | undefined) =>
  (optionProps: React.HTMLAttributes<HTMLLIElement>, option: T, state: AutocompleteRenderOptionState) => {
    const { key, ...itemProps } = optionProps as OptionItemProps;
    const content = renderOption === undefined ? getOptionLabel(option) : renderOption(option, { selected: state.selected });
    return (
      <li key={key} {...itemProps}>
        {content}
      </li>
    );
  };

export const chipRenderer =
  <T,>(getOptionKey: (option: T) => OptionKey, getOptionLabel: (option: T) => string, size: FieldSize) =>
  (chosen: T[], getTagProps: AutocompleteRenderGetTagProps) =>
    chosen.map((option, index) => {
      const { key: _position, ...tagProps } = getTagProps({ index });
      return <Chip key={getOptionKey(option)} label={getOptionLabel(option)} size={size} {...tagProps} />;
    });
